import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * How long the hover pause/resume takes to ease in, in ms. Defaults to
   * ANIMATION_OPTIONS.duration; override per-instance with
   * `data-hover-transition-duration="0"` for an immediate pause on hover.
   */
  get #hoverTransitionDuration() {
    const value = Number(this.getAttribute('data-hover-transition-duration'));
    return Number.isFinite(value) && value >= 0 ? value : ANIMATION_OPTIONS.duration;
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    if (this.#hoverTransitionDuration === 0) {
      animation.updatePlaybackRate(0);
      return;
    }

    this.#animation = animateValue({
      duration: this.#hoverTransitionDuration,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, this.#hoverTransitionDuration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    if (this.#hoverTransitionDuration === 0) {
      this.#animation?.cancel();
      this.#animation = null;
      animation.updatePlaybackRate(1);
      return;
    }

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      duration: this.#hoverTransitionDuration,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.#hideFromAssistiveTech(clone);

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (wrapper.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      this.#hideFromAssistiveTech(clone);
      content.appendChild(clone);
    }
  }

  /**
   * Keeps a duplicated marquee clone out of screen readers and the keyboard tab order,
   * without disabling pointer interaction — a sighted user can still click any visible
   * copy of a card (e.g. a product link) as it scrolls past, since `inert` would otherwise
   * make every duplicate unclickable.
   * @param {HTMLElement} clone
   */
  #hideFromAssistiveTech(clone) {
    for (const focusable of clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
      focusable.setAttribute('tabindex', '-1');
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  /**
   * Maximum number of individual items (children of one repeated unit) that
   * may exist across all duplicated (non-original) copies combined. Unset by
   * default, which preserves the existing "fill the viewport" behavior;
   * opt in per-instance with e.g. `data-max-duplicate-items="10"`.
   */
  get #maxDuplicateItems() {
    const value = Number(this.getAttribute('data-max-duplicate-items'));
    return Number.isFinite(value) && value > 0 ? value : Infinity;
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const firstGroup = content.firstElementChild;
    const marqueeRepeatedItemWidth = firstGroup instanceof HTMLElement ? firstGroup.offsetWidth : 0;
    const neededCopies = Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);

    const maxDuplicateItems = this.#maxDuplicateItems;
    const itemsPerGroup = firstGroup instanceof HTMLElement ? firstGroup.children.length : 0;

    if (itemsPerGroup > 0 && Number.isFinite(maxDuplicateItems)) {
      const maxAdditionalCopies = Math.floor(maxDuplicateItems / itemsPerGroup);
      return Math.min(neededCopies, maxAdditionalCopies + 1);
    }

    return neededCopies;
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
